import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router';
import {useSnackbar} from 'notistack';
import {
    Alert,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import {Add, Delete, Edit, Refresh} from '@mui/icons-material';
import dayjs from 'dayjs';
import {useApplicationContext} from 'ApplicationContext';
import {useDocumentTitle} from 'Hooks/useDocumentTitle';
import {XmlExampleStatus, XmlExampleSummaryDto} from 'apiClient/Api';

const STATUS_FILTER_ALL = 'all' as const;
type StatusFilter = typeof STATUS_FILTER_ALL | XmlExampleStatus;

const statusChipColor = (status: XmlExampleStatus | undefined): 'success' | 'warning' | 'default' => {
    switch (status) {
        case XmlExampleStatus.Approved:
            return 'success';
        case XmlExampleStatus.Draft:
            return 'warning';
        case XmlExampleStatus.Deprecated:
        default:
            return 'default';
    }
};

/**
 * Lists all XML examples with status filter, snippet count and quick actions.
 * Each card opens the dedicated editor for full edit / Monaco-based snippet
 * authoring.
 */
const XmlExamplesList: React.FC = () => {
    const {t} = useTranslation();
    useDocumentTitle('xmlExamples.list.title');
    const context = useApplicationContext();
    const navigate = useNavigate();
    const {enqueueSnackbar} = useSnackbar();

    const [items, setItems] = useState<XmlExampleSummaryDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>(STATUS_FILTER_ALL);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await context.api.xmlExample.xmlExampleList(
                statusFilter === STATUS_FILTER_ALL ? undefined : {status: statusFilter},
            );
            setItems(response.data ?? []);
        } catch (e) {
            setError(t('xmlExamples.list.loadError'));
        } finally {
            setLoading(false);
        }
    }, [context.api.xmlExample, statusFilter, t]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const sortedItems = useMemo(
        () => [...items].sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '')),
        [items],
    );

    const handleDelete = async (id: string, title: string | undefined) => {
        if (!window.confirm(t('xmlExamples.list.confirmDelete', {title: title ?? '-'}))) return;
        setDeletingId(id);
        try {
            await context.api.xmlExample.xmlExampleDelete(id);
            enqueueSnackbar(t('xmlExamples.list.deleted'), {variant: 'success'});
            await fetchData();
        } catch (e) {
            enqueueSnackbar(t('xmlExamples.list.deleteError'), {variant: 'error'});
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <Box sx={{width: '100%', p: 2}}>
            <Stack
                direction={{xs: 'column', sm: 'row'}}
                spacing={2}
                sx={{alignItems: {xs: 'stretch', sm: 'center'}, justifyContent: 'space-between', mb: 3}}
            >
                <Box>
                    <Typography variant="h4" component="h1">
                        {t('xmlExamples.list.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('xmlExamples.list.subtitle')}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <FormControl size="small" sx={{minWidth: 160}}>
                        <InputLabel id="xml-examples-status-filter">
                            {t('xmlExamples.list.statusFilter')}
                        </InputLabel>
                        <Select
                            labelId="xml-examples-status-filter"
                            label={t('xmlExamples.list.statusFilter')}
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                        >
                            <MenuItem value={STATUS_FILTER_ALL}>{t('xmlExamples.list.statusAll')}</MenuItem>
                            <MenuItem value={XmlExampleStatus.Draft}>{t('xmlExamples.status.draft')}</MenuItem>
                            <MenuItem value={XmlExampleStatus.Approved}>{t('xmlExamples.status.approved')}</MenuItem>
                            <MenuItem value={XmlExampleStatus.Deprecated}>{t('xmlExamples.status.deprecated')}</MenuItem>
                        </Select>
                    </FormControl>
                    <Tooltip title={t('xmlExamples.list.refresh')}>
                        <span>
                            <IconButton onClick={fetchData} disabled={loading}>
                                {loading ? <CircularProgress size={20}/> : <Refresh/>}
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Button
                        variant="contained"
                        startIcon={<Add/>}
                        onClick={() => navigate('/admin/xml-examples/new')}
                    >
                        {t('xmlExamples.list.newButton')}
                    </Button>
                </Stack>
            </Stack>

            {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}

            {loading && items.length === 0 && (
                <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
                    <CircularProgress/>
                </Box>
            )}

            {!loading && sortedItems.length === 0 && !error && (
                <Card variant="outlined">
                    <CardContent>
                        <Typography color="text.secondary" sx={{textAlign: 'center', py: 4}}>
                            {t('xmlExamples.list.empty')}
                        </Typography>
                    </CardContent>
                </Card>
            )}

            <Grid container spacing={2}>
                {sortedItems.map(item => (
                    <Grid key={item.id} size={{xs: 12, sm: 6, md: 4, lg: 3}} sx={{display: 'flex'}}>
                        <Card
                            variant="outlined"
                            sx={{width: '100%', display: 'flex', flexDirection: 'column'}}
                        >
                            <CardActionArea
                                onClick={() => item.id && navigate(`/admin/xml-examples/${item.id}`)}
                                sx={{flex: 1, alignItems: 'stretch', display: 'flex'}}
                            >
                                <CardContent sx={{flex: 1, display: 'flex', flexDirection: 'column', gap: 1}}>
                                    <Stack direction="row" spacing={1} sx={{justifyContent: 'space-between', alignItems: 'flex-start'}}>
                                        <Typography variant="h6" sx={{lineHeight: 1.2, wordBreak: 'break-word', flex: 1}}>
                                            {item.title ?? '—'}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={t(`xmlExamples.status.${XmlExampleStatus[item.status ?? XmlExampleStatus.Draft].toLowerCase()}`)}
                                            color={statusChipColor(item.status)}
                                            sx={{flexShrink: 0, fontWeight: 600}}
                                        />
                                    </Stack>

                                    {item.description && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {item.description}
                                        </Typography>
                                    )}

                                    {(item.features?.length ?? 0) > 0 && (
                                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5}}>
                                            {item.features!.slice(0, 4).map(f => (
                                                <Chip key={f} label={f} size="small" variant="outlined"/>
                                            ))}
                                            {(item.features!.length ?? 0) > 4 && (
                                                <Chip label={`+${item.features!.length - 4}`} size="small" variant="outlined"/>
                                            )}
                                        </Box>
                                    )}

                                    <Box sx={{flex: 1}}/>

                                    <Stack direction="row" spacing={1} sx={{flexWrap: 'wrap', rowGap: 0.5, mt: 1}}>
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            label={t('xmlExamples.list.snippetCount', {count: item.snippetCount ?? 0})}
                                        />
                                        {item.targetVersion && (
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={`${t('xmlExamples.fields.targetVersion')}: ${item.targetVersion}`}
                                            />
                                        )}
                                    </Stack>

                                    <Typography variant="caption" color="text.secondary">
                                        {t('xmlExamples.list.updated')}: {item.updatedAt ? dayjs(item.updatedAt).format('DD.MM.YYYY HH:mm') : '—'}
                                    </Typography>
                                </CardContent>
                            </CardActionArea>

                            <Stack direction="row" spacing={0.5} sx={{px: 1, pb: 1, justifyContent: 'flex-end'}}>
                                <Tooltip title={t('xmlExamples.list.edit')}>
                                    <IconButton
                                        size="small"
                                        onClick={() => item.id && navigate(`/admin/xml-examples/${item.id}`)}
                                    >
                                        <Edit fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t('xmlExamples.list.delete')}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            disabled={deletingId === item.id}
                                            onClick={() => item.id && handleDelete(item.id, item.title)}
                                        >
                                            {deletingId === item.id
                                                ? <CircularProgress size={16}/>
                                                : <Delete fontSize="small"/>}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default XmlExamplesList;
